// Komendy do urządzeń przez bramę (docs/pwa/PROJEKT.md 6): `POST /brama/urzadzenia/{sn}/komenda`. Wynik od razu
// (odrzucono, „już ustawiona”, „Wysłano”) albo 202 i wynik przez SSE (`wynik`, tylko telefonom osoby, która wysłała).
// Stan komendy żyje poza Reactem, per sztuka i nazwa - przycisk pokazuje „⏳” do wyniku, wartość na ekranie zmienia
// dopiero echo urządzenia w widoku (KN-28, WS-11). Kopia bramy, która padnie w trakcie czekania, wyniku nie poda -
// po oknie potwierdzenia telefon sam pokazuje „brak potwierdzenia”.
import { useCallback, useSyncExternalStore } from "react";
import { Haptics, NotificationType } from "@capacitor/haptics";
import type { CmdResult, CommandReply, StreamEvent } from "../gen/kontrakt";
import { BladZaplecza, opisBledu, zapytanie } from "../api/zaplecze";
import { t } from "../i18n/t";
import { natywna } from "../uklad/instalacja";

export type StanKomendy = { stan: "w_toku" } | { stan: "wynik"; wynik: CmdResult };

type Sluchacz = () => void;

const stany = new Map<string, StanKomendy>();
const sluchacze = new Map<string, Set<Sluchacz>>();
const czekajace = new Map<string, { klucz: string; zegar: number }>();
// Ostatnio wysłana komenda per sztuka i nazwa - „Ponów” przy braku potwierdzenia wysyła ją jeszcze raz.
const ostatnie = new Map<string, { sn: string; nazwa: string; wartosc: unknown; czasS: number }>();

const klucz = (sn: string, nazwa: string) => sn + "\u0000" + nazwa;

function ustaw(k: string, s: StanKomendy | undefined): void {
  if (s) stany.set(k, s);
  else stany.delete(k);
  const z = sluchacze.get(k);
  if (z) for (const f of z) f();
}

// Wibracja przy wyniku (aplikacja ze sklepu, USTALENIA.md „Aplikacja natywna”): wykonano / wysłano, skorygowano,
// reszta - błąd. Wynik przychodzi tylko telefonowi, z którego wysłano komendę.
const WIBRACJA: Partial<Record<CmdResult["status"], NotificationType>> = {
  wykonano: NotificationType.Success, wyslano: NotificationType.Success, skorygowano: NotificationType.Warning,
};

function wynik(k: string, w: CmdResult): void {
  ustaw(k, { stan: "wynik", wynik: w });
  if (natywna()) void Haptics.notification({ type: WIBRACJA[w.status] ?? NotificationType.Error }).catch(() => undefined);
}

/**
 * Wysyła komendę. `czasS` - okno potwierdzenia z opisu linii (`czas_potwierdzenia_s`); STOP wędzarni ponawia
 * brakującą część raz, więc telefon czeka dwa okna i chwilę zapasu.
 */
export async function wyslij(sn: string, nazwa: string, wartosc: unknown, czasS: number): Promise<void> {
  const k = klucz(sn, nazwa);
  if (stany.get(k)?.stan === "w_toku") return;
  ostatnie.set(k, { sn, nazwa, wartosc, czasS });
  ustaw(k, { stan: "w_toku" });
  try {
    const odp = await zapytanie<CommandReply>("POST", `/brama/urzadzenia/${encodeURIComponent(sn)}/komenda`,
      { nazwa, wartosc: wartosc ?? null });
    if (odp.wynik) return wynik(k, odp.wynik);
    if (!odp.id) return wynik(k, { id: "", user: 0, nazwa, status: "odrzucono", komunikat: opisBledu(null) });
    const id = odp.id;
    const zegar = window.setTimeout(() => {
      czekajace.delete(id);
      wynik(k, { id, user: 0, nazwa, status: "brak_potwierdzenia" });
    }, (czasS * 2 + 5) * 1000);
    czekajace.set(id, { klucz: k, zegar });
  } catch (b) {
    const odrzucona = b instanceof BladZaplecza ? (b.dane as CommandReply | null)?.wynik : null;
    wynik(k, odrzucona ?? { id: "", user: 0, nazwa, status: "odrzucono", komunikat: opisBledu(b) });
  }
}

/** Wynik z SSE bramy (`wynik`). Wynik komendy wysłanej z innego telefonu tej osoby - pomijany. */
export function przyjmijWynik(e: StreamEvent): void {
  const w = e.dane as CmdResult;
  const c = czekajace.get(w.id);
  if (!c) return;
  window.clearTimeout(c.zegar);
  czekajace.delete(w.id);
  wynik(c.klucz, w);
}

/**
 * „Ponów” - ta sama komenda jeszcze raz (decyduje user, brama sama nie ponawia - PROJEKT.md 6). Komenda złożona
 * (wynik z `pola`, WS-2) - tylko pola bez potwierdzenia.
 */
export function ponow(sn: string, nazwa: string): void {
  const k = klucz(sn, nazwa);
  const o = ostatnie.get(k);
  if (!o) return;
  const s = stany.get(k);
  let wartosc = o.wartosc;
  if (s?.stan === "wynik" && s.wynik.pola && wartosc && typeof wartosc === "object") {
    const pelna = wartosc as Record<string, unknown>;
    wartosc = Object.fromEntries(s.wynik.pola.filter((p) => p.status === "brak_potwierdzenia")
      .map((p) => [p.nazwa, pelna[p.nazwa]]));
  }
  void wyslij(o.sn, o.nazwa, wartosc, o.czasS);
}

/** Zamknięcie komunikatu wyniku (komenda w toku zostaje). */
export function zapomnij(sn: string, nazwa: string): void {
  const k = klucz(sn, nazwa);
  if (stany.get(k)?.stan === "wynik") ustaw(k, undefined);
}

/** Wylogowanie: bez stanów i czekania. */
export function wyczyscKomendy(): void {
  for (const c of czekajace.values()) window.clearTimeout(c.zegar);
  czekajace.clear();
  ostatnie.clear();
  for (const k of [...stany.keys()]) ustaw(k, undefined);
}

export function useKomenda(sn: string, nazwa: string): StanKomendy | undefined {
  const k = klucz(sn, nazwa);
  const sub = useCallback((s: Sluchacz) => {
    let z = sluchacze.get(k);
    if (!z) sluchacze.set(k, (z = new Set()));
    z.add(s);
    return () => {
      z.delete(s);
      if (z.size === 0) sluchacze.delete(k);
    };
  }, [k]);
  const get = useCallback(() => stany.get(k), [k]);
  return useSyncExternalStore(sub, get);
}

/** Tekst wyniku dla człowieka (komunikat bramy po polsku albo opis statusu). */
export function opisWyniku(w: CmdResult): string {
  if (w.komunikat) return t(w.komunikat);
  switch (w.status) {
    case "wykonano":
      return t("Zapisano.");
    case "wyslano":
      return t("Wysłano.");
    case "skorygowano":
      return t("Sterownik ustawił {v}.", { v: String(w.wartosc ?? "") });
    case "brak_potwierdzenia":
      return t("Sterownik nie potwierdził zmiany.");
    default:
      return t("Komenda nie została wykonana.");
  }
}
